import { usePage } from "@inertiajs/react";
import { BizminerSidebar } from "@/bizminer/layouts/app/BizminerSidebar.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar.tsx";
import { SimpleThemeToggle } from "@/components/ui/simple-theme-toggle.tsx";
import { useDarkMode } from "@/hooks/use-dark-mode.tsx";
import type { BizminerInertiaSharedProps } from "@/types/inertia.ts";
import { Breadcrumbs } from "../Breadcrumbs.tsx";

const PageHeader = () => {
  const { theme, toggle } = useDarkMode();

  return (
    <header className="border-b flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
      <div className="flex items-center justify-between w-full gap-2 px-4">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <Breadcrumbs />
        </div>
        <div className="flex items-center gap-2">
          <SimpleThemeToggle theme={theme} onCycle={toggle} />
        </div>
      </div>
    </header>
  );
};

interface BizminerLayoutProps {
  children: React.ReactNode;
}

export default function BizminerLayout({ children }: BizminerLayoutProps) {
  const page = usePage<BizminerInertiaSharedProps>();
  const component = page.component;
  const sidebar = page.props.sidebar;

  // Detect if we have account context from session/URL
  const hasAccountContext = !!sidebar?.current_account;

  // Detect if we're on an account-scoped page
  const isAccountPage =
    component.startsWith("Bizminer/App/Accounts/") ||
    component.startsWith("Bizminer/App/PillarInsights/");

  return (
    <SidebarProvider>
      <BizminerSidebar
        hasAccountContext={hasAccountContext}
        isAccountPage={isAccountPage}
        variant="inset"
      />
      <SidebarInset>
        <PageHeader />
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0 w-full fixed:2xl:mx-auto fixed:2xl:max-w-7xl min-w-0">
          <div className="min-h-[100vh] flex-1 rounded-xl md:min-h-min w-full min-w-0 overflow-x-auto">
            <div className="p-4 lg:p-6 w-full">{children}</div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
